import { useEffect, useMemo, useRef, useState } from "react";
import { useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import { trackEvent } from "@hopper-b2b/api";
import {
  MODAL_ALERT,
  ModalCategoryType,
  ModalScreens,
} from "@hopper-b2b/types";
import type { IErrorModal } from "@hopper-b2b/ui";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  Button,
  ButtonTypeEnum,
} from "@tenant/ui";
import clsx from "clsx";
import styles from "./ErrorModal.module.scss";

/**
 * Maps ProductType to the corresponding ModalScreens value for tracking
 */
const getScreenFromProductType = (productType?: ProductType): ModalScreens => {
  switch (productType) {
    case ProductType.FLIGHTS:
      return ModalScreens.FLIGHTS_CHECKOUT;
    case ProductType.LODGING:
      return ModalScreens.HOTELS_CHECKOUT;
    case ProductType.CARS:
      return ModalScreens.CARS_CHECKOUT;
    case ProductType.ACTIVITIES:
      return ModalScreens.ACTIVITIES_CHECKOUT;
    case ProductType.PACKAGE:
      return ModalScreens.PACKAGE_CHECKOUT;
    default:
      // Fallback to flights checkout for backwards compatibility
      return ModalScreens.FLIGHTS_CHECKOUT;
  }
};

interface IErrorModalProps extends Omit<IErrorModal, "productType"> {
  productType: ProductType;
}

export const ErrorModal = (props: IErrorModalProps): JSX.Element => {
  const {
    primaryButtonText,
    primaryOnClick,
    secondaryButtonText,
    secondaryOnClick,
    title,
    subtitle,
    open,
    trackingEventProperties,
    className,
    onClose,
    productType,
    modalType,
  } = props;

  const [block, cn] = useModuleBEM(styles, "ErrorModal");

  const [isOpen, setIsOpen] = useState(props.open);
  const hasTracked = useRef(false);

  const hasSecondaryButton = secondaryButtonText && secondaryOnClick;
  const modalEventProperties = useMemo(
    () => ({
      ...trackingEventProperties,
      modal_title: title,
      modal_subtitle: subtitle,
      primary_button: primaryButtonText,
      secondary_button: hasSecondaryButton ? secondaryButtonText : undefined,
      screen: getScreenFromProductType(productType),
      category: ModalCategoryType.TROUBLE,
      product_type: productType,
      modal_type: modalType,
    }),
    [
      hasSecondaryButton,
      modalType,
      primaryButtonText,
      productType,
      secondaryButtonText,
      subtitle,
      title,
      trackingEventProperties,
    ]
  );

  // Sync internal state with open prop
  useEffect(() => {
    setIsOpen(open);
    // Reset tracking flag when modal closes
    if (!open) {
      hasTracked.current = false;
    }
  }, [open]);

  // Track modal open events (only once per open)
  useEffect(() => {
    if (open && !hasTracked.current) {
      hasTracked.current = true;
      trackEvent({
        eventName: MODAL_ALERT,
        properties: modalEventProperties,
      });
    }
  }, [modalEventProperties, open]);

  const handleClose = () => {
    setIsOpen(false);
    onClose?.();
  };

  return (
    <AlertDialog open={isOpen} onClose={handleClose}>
      <AlertDialogContent
        className={clsx(block, className)}
        title={title}
        description={subtitle}
        status="error"
        actions={
          <>
            <AlertDialogAction asChild>
              <Button
                onClick={() => {
                  primaryOnClick();
                  setIsOpen(false);
                }}
                className={cn("button")}
                buttonType={ButtonTypeEnum.Primary}
              >
                {primaryButtonText}
              </Button>
            </AlertDialogAction>
            {hasSecondaryButton && (
              <AlertDialogAction asChild>
                <Button
                  onClick={() => {
                    secondaryOnClick();
                    setIsOpen(false);
                  }}
                  className={cn("button")}
                  buttonType={ButtonTypeEnum.Tertiary}
                >
                  {secondaryButtonText}
                </Button>
              </AlertDialogAction>
            )}
          </>
        }
      ></AlertDialogContent>
    </AlertDialog>
  );
};
